/**
 * Promo code service -- validate, calculate discount, record usage.
 * Class-based singleton per architecture rules.
 */
import { supabase } from '../supabase'
import type { Cols } from '../types/db-rows'
import { ServiceError } from '../errors/service-error'
import { isProfileComplete } from '../utils/profile-completion'

interface PromoValidationResult {
  valid: boolean
  promoCodeId?: string
  discountCents?: number
  discountType?: 'percent' | 'fixed'
  discountValue?: number
  description?: string
  reason?: string
}

type PromoProfileRow = Cols<
  'profiles',
  | 'first_name'
  | 'last_name'
  | 'phone'
  | 'avatar_url'
  | 'date_of_birth'
  | 'country_id'
  | 'city_id'
  | 'preferred_locale'
  | 'is_verified'
>

function mapPromoProfile(row: PromoProfileRow | null) {
  if (!row) return null
  return {
    firstName: row.first_name,
    lastName: row.last_name,
    phone: row.phone,
    avatarUrl: row.avatar_url,
    dateOfBirth: row.date_of_birth,
    countryId: row.country_id,
    cityId: row.city_id,
    preferredLocale: row.preferred_locale,
    isVerified: row.is_verified,
  }
}

class PromoServiceImpl {
  /**
   * Validate a promo code for a specific user and rental amount.
   * Checks: existence, active status, date range, usage limits,
   * first-rental-only, minimum amount, and calculates discount.
   */
  async validatePromoCode(
    code: string,
    userId: string,
    rentalSubtotalCents: number,
  ): Promise<PromoValidationResult> {
    const normalized = code.trim().toUpperCase()
    if (!normalized) return { valid: false, reason: 'EMPTY_CODE' }

    let userProfile: PromoProfileRow | null | undefined
    const getUserProfile = async (): Promise<PromoProfileRow | null> => {
      if (userProfile !== undefined) return userProfile
      // first_name, phone and date_of_birth are no longer readable from the
      // table by any role. This is the caller's own row, so it comes from the
      // definer RPC.
      void userId
      const { data, error: profileError } = await supabase.rpc('current_profile')
      if (profileError) throw ServiceError.fromSupabaseError(profileError)
      userProfile = data
      return userProfile
    }

    // Fetch promo code
    const { data: promo, error } = await supabase
      .from('promo_codes')
      .select('*')
      .eq('code', normalized)
      .eq('is_active', true)
      .single()

    if (error || !promo) {
      return { valid: false, reason: 'CODE_NOT_FOUND' }
    }

    const now = new Date().toISOString()

    // Check date validity
    if (promo.valid_from && now < promo.valid_from) {
      return { valid: false, reason: 'NOT_YET_VALID' }
    }
    if (promo.valid_until && now > promo.valid_until) {
      return { valid: false, reason: 'EXPIRED' }
    }

    // Check global usage limit
    if (promo.max_uses && promo.use_count >= promo.max_uses) {
      return { valid: false, reason: 'MAX_USES_REACHED' }
    }

    if (promo.user_ids && promo.user_ids.length > 0 && !promo.user_ids.includes(userId)) {
      return { valid: false, reason: 'USER_NOT_ELIGIBLE' }
    }

    // Check per-user usage limit
    if (promo.max_uses_per_user) {
      const { count } = await supabase
        .from('promo_code_uses')
        .select('id', { count: 'exact', head: true })
        .eq('promo_code_id', promo.id)
        .eq('profile_id', userId)

      if ((count ?? 0) >= promo.max_uses_per_user) {
        return { valid: false, reason: 'USER_LIMIT_REACHED' }
      }
    }

    // Check first rental only.
    //
    // "First rental" has to mean the first one they PAID for. Counting the
    // status alone denies a welcome discount to someone who never actually
    // rented — the failure runs in the guest's disfavour, which is the kind
    // nobody reports and everybody resents. The inner join drops a completed
    // rental with no captured payment instead of counting it against them.
    if (promo.first_rental_only) {
      const { count } = await supabase
        .from('rentals')
        .select('id, payments!inner(status)', { count: 'exact', head: true })
        .eq('guest_id', userId)
        .eq('status', 'completed')
        .eq('payments.status', 'captured')

      if ((count ?? 0) > 0) {
        return { valid: false, reason: 'NOT_FIRST_RENTAL' }
      }
    }

    // Check minimum rental amount
    if (promo.min_rental_cents && rentalSubtotalCents < promo.min_rental_cents) {
      return { valid: false, reason: 'BELOW_MINIMUM' }
    }

    if (normalized === 'WELCOME') {
      const profile = mapPromoProfile(await getUserProfile())
      if (!isProfileComplete(profile)) {
        return { valid: false, reason: 'PROFILE_INCOMPLETE' }
      }
    }

    // Country restriction. Previous version was a
    // stub ("assume SV") which let regional promo codes leak across
    // borders — a code targeting Guatemala-only could be redeemed by an
    // SV-locale guest. Now we look up the redeeming user's profile
    // country and reject when it isn't on the allow-list.
    if (promo.country_ids && promo.country_ids.length > 0) {
      const profile = await getUserProfile()
      const userCountry = profile?.country_id ?? null
      if (!userCountry || !promo.country_ids.includes(userCountry)) {
        return { valid: false, reason: 'COUNTRY_NOT_ELIGIBLE' }
      }
    }

    // Calculate discount
    let discountCents: number
    if (promo.discount_type === 'percent') {
      discountCents = Math.round((rentalSubtotalCents * promo.discount_value) / 100)
    } else {
      discountCents = promo.discount_value
    }

    // Cap at max_discount_cents if set
    if (promo.max_discount_cents && discountCents > promo.max_discount_cents) {
      discountCents = promo.max_discount_cents
    }

    return {
      valid: true,
      promoCodeId: promo.id,
      discountCents,
      discountType: promo.discount_type as 'percent' | 'fixed',
      discountValue: promo.discount_value,
      description: promo.description ?? undefined,
    }
  }

  /**
   * Record promo code usage after a successful booking.
   *
   * Delegates to the SECURITY DEFINER record_promo_use() RPC (migration
   * 20260422000001) which INSERTs the usage row and increments use_count in
   * a single transaction, guarded by max_uses. Closes the prior
   * read-modify-write race where two concurrent redemptions could exceed
   * max_uses by one.
   *
   * Throws ServiceError('promo_exhausted') if the code's global cap is
   * already reached. The UNIQUE(promo_code_id, rental_id) constraint makes
   * double-recording for the same rental an idempotent no-op.
   */
  async recordUsage(
    promoCodeId: string,
    profileId: string,
    rentalId: string,
    discountCents: number,
  ): Promise<void> {
    const { error } = await supabase.rpc('record_promo_use', {
      p_promo_id: promoCodeId,
      p_profile_id: profileId,
      p_rental_id: rentalId,
      p_discount_cents: discountCents,
    })
    if (!error) return

    // Duplicate INSERT for the same rental — treat as idempotent success.
    const code = error.code
    if (code === '23505') return

    // Atomic cap hit: the RPC raises P0001 with message 'promo_exhausted'.
    const errMsg = error.message.toLowerCase()
    if (code === 'P0001' && errMsg.includes('promo_profile_incomplete')) {
      throw new ServiceError({
        code: 'PROMO_PROFILE_INCOMPLETE',
        message: 'Complete your profile before using this promo code.',
        statusCode: 403,
        userMessageKey: 'booking.promo_profile_incomplete',
      })
    }
    if (code === 'P0001' && errMsg.includes('promo_user_not_eligible')) {
      throw new ServiceError({
        code: 'PROMO_USER_NOT_ELIGIBLE',
        message: 'This promo code is not available for this profile.',
        statusCode: 403,
        userMessageKey: 'booking.promo_user_not_eligible',
      })
    }
    if (code === 'P0001' && errMsg.includes('promo_exhausted')) {
      throw new ServiceError({
        code: 'PROMO_EXHAUSTED',
        message: 'This promo code has reached its usage limit.',
        statusCode: 409,
      })
    }

    throw ServiceError.fromSupabaseError(error)
  }
}

export const promoService = new PromoServiceImpl()
